import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getToken, setToken as persistToken, clearToken } from '../utils/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getToken());

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = getToken();
      if (savedToken) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (err) {
          clearToken();
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
      email,
      password
    });

    if (response.data.success) {
      persistToken(response.data.token);
      setToken(response.data.token);
      setUser(response.data.user);
      return { success: true };
    }
    return { success: false, error: 'Login failed' };
  };

  const register = async (name, email, password, language = 'fr') => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
      name,
      email,
      password,
      language
    });

    if (response.data.success) {
      // Auto login after registration
      return login(email, password);
    }
    return { success: false, error: 'Registration failed' };
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      // Log for observability but don't block logout UX.
      console.debug('[auth] server-side logout call failed:', err?.message);
    }
    clearToken();
    setToken(null);
    setUser(null);
  };

  const saveSimulation = async (name, data) => {
    if (!token) return { success: false, error: 'Not authenticated' };

    const response = await axios.post(`${BACKEND_URL}/api/user/simulations`,
      { name, data },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const getSimulations = async () => {
    if (!token) return { simulations: [] };

    const response = await axios.get(`${BACKEND_URL}/api/user/simulations`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const deleteSimulation = async (simulationId) => {
    if (!token) return { success: false };

    await axios.delete(`${BACKEND_URL}/api/user/simulations/${simulationId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return { success: true };
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      saveSimulation,
      getSimulations,
      deleteSimulation
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
